import { Button, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useEffect, useRef } from 'react';

import type { GetTestRunAttemptType, GetTestRunType } from '~/server/api/routers/testRun';
import DiffViewer from '../test/diffViewer/DiffViewer';
import { VersionBadge } from '../common/VersionBadge';

// uses dynamically updating modals: https://mantine.dev/x/modals/#dynamic-content-and-the-modals-manager

export default function TestRunHookDiffViews({
  testRunAttempt,
  testRun,
}: {
  testRunAttempt?: GetTestRunAttemptType;
  testRun: GetTestRunType;
}) {
  const beforeEachModalId = useRef<string | undefined>(undefined);
  const afterEachModalId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (beforeEachModalId.current && testRunAttempt?.outputBeforeEachTestContent) {
      modals.updateModal({
        modalId: beforeEachModalId.current,
        children: (
          <DiffViewer
            testId={testRun.testId}
            prevContent={{
              testContentId: testRunAttempt?.inputBeforeEachTestContentId || '',
              steps: testRunAttempt?.inputBeforeEachTestContent?.steps ?? [],
            }}
            newContent={{
              testContentId: testRunAttempt?.outputBeforeEachTestContentId || '',
              steps: testRunAttempt?.outputBeforeEachTestContent?.steps ?? [],
            }}
            testRunOutput={
              testRunAttempt?.executionOutput ?? {
                aiChanges: [],
                errors: [],
                logs: [],
                conditionalBlockResults: [],
                erroredAutoHealableOperations: [],
                aiActionLogs: [],
              }
            }
            canAcceptChanges={true}
            prevStepsTitle={
              <>
                <Text className="font-medium">Original Test</Text>
                <VersionBadge
                  contentId={testRunAttempt?.inputBeforeEachTestContentId || ''}
                  trunkContentId={testRunAttempt.outputBeforeEachTestContent?.test?.trunkContentId || ''}
                  testId={testRunAttempt.outputBeforeEachTestContent?.test?.id || ''}
                />
              </>
            }
            newStepsTitle={<Text className="font-medium">AI-modified Test</Text>}
            gitBranch={testRun.testSuiteRun?.gitBranch ?? undefined}
          />
        ),
      });
    }

    if (afterEachModalId.current && testRunAttempt?.outputAfterEachTestContent) {
      modals.updateModal({
        modalId: afterEachModalId.current,
        children: (
          <DiffViewer
            testId={testRun.testId}
            prevContent={{
              testContentId: testRunAttempt?.inputAfterEachTestContentId || '',
              steps: testRunAttempt?.inputAfterEachTestContent?.steps ?? [],
            }}
            newContent={{
              testContentId: testRunAttempt?.outputAfterEachTestContentId || '',
              steps: testRunAttempt?.outputAfterEachTestContent?.steps ?? [],
            }}
            testRunOutput={
              testRunAttempt?.executionOutput ?? {
                aiChanges: [],
                errors: [],
                logs: [],
                conditionalBlockResults: [],
                erroredAutoHealableOperations: [],
                aiActionLogs: [],
              }
            }
            canAcceptChanges={true}
            prevStepsTitle={
              <>
                <Text className="font-medium">Original Test</Text>
                <VersionBadge
                  contentId={testRunAttempt?.inputAfterEachTestContentId || ''}
                  trunkContentId={testRunAttempt.outputAfterEachTestContent?.test?.trunkContentId || ''}
                  testId={testRunAttempt.outputAfterEachTestContent?.test?.id || ''}
                />
              </>
            }
            newStepsTitle={<Text className="font-medium">AI-modified Test</Text>}
            gitBranch={testRun.testSuiteRun?.gitBranch ?? undefined}
          />
        ),
      });
    }
  }, [testRunAttempt, testRun.testSuiteRun?.gitBranch]);

  return (
    <>
      {!!testRunAttempt?.outputBeforeEachTestContent && (
        <Button
          size="xs"
          variant="light"
          color="blue"
          onClick={() => {
            beforeEachModalId.current = modals.open({
              title: 'BeforeEach Test',
              size: 'auto',
              // Show in front of test run results drawer
              zIndex: 1000,
              children: (
                <DiffViewer
                  testId={testRun.testId}
                  // We compare the latest test run attempt (master) with the selected test run attempt (AI-modified)
                  prevContent={{
                    testContentId: testRunAttempt?.inputBeforeEachTestContentId || '',
                    steps: testRunAttempt?.inputBeforeEachTestContent?.steps ?? [],
                  }}
                  newContent={{
                    testContentId: testRunAttempt?.outputBeforeEachTestContentId || '',
                    steps: testRunAttempt?.outputBeforeEachTestContent?.steps ?? [],
                  }}
                  testRunOutput={
                    testRunAttempt?.executionOutput ?? {
                      aiChanges: [],
                      errors: [],
                      logs: [],
                      conditionalBlockResults: [],
                      erroredAutoHealableOperations: [],
                    }
                  }
                  canAcceptChanges={true}
                  prevStepsTitle={
                    <>
                      <Text className="font-medium">Original Test</Text>
                      <VersionBadge
                        contentId={testRunAttempt?.inputBeforeEachTestContentId || ''}
                        trunkContentId={testRunAttempt.outputBeforeEachTestContent?.test?.trunkContentId || ''}
                        testId={testRunAttempt.outputBeforeEachTestContent?.test?.id || ''}
                      />
                    </>
                  }
                  newStepsTitle={<Text className="font-medium">AI-modified Test</Text>}
                  gitBranch={testRun.testSuiteRun?.gitBranch ?? undefined}
                  testRunAttemptId={testRunAttempt?.id}
                />
              ),
            });
          }}
          className="mr-2"
        >
          BeforeEach
        </Button>
      )}
      {!!testRunAttempt?.outputAfterEachTestContent && (
        <Button
          size="xs"
          variant="light"
          color="blue"
          onClick={() => {
            afterEachModalId.current = modals.open({
              title: 'AfterEach Test',
              size: 'auto',
              zIndex: 1000,
              children: (
                <DiffViewer
                  testId={testRun.testId}
                  // We compare the latest test run attempt (master) with the selected test run attempt (AI-modified)
                  prevContent={{
                    testContentId: testRunAttempt?.inputAfterEachTestContentId || '',
                    steps: testRunAttempt?.inputAfterEachTestContent?.steps ?? [],
                  }}
                  newContent={{
                    testContentId: testRunAttempt?.outputAfterEachTestContentId || '',
                    steps: testRunAttempt?.outputAfterEachTestContent?.steps ?? [],
                  }}
                  testRunOutput={
                    testRunAttempt?.executionOutput ?? {
                      aiChanges: [],
                      errors: [],
                      logs: [],
                      conditionalBlockResults: [],
                      erroredAutoHealableOperations: [],
                    }
                  }
                  canAcceptChanges={true}
                  prevStepsTitle={
                    <>
                      <Text className="font-medium">Original Test</Text>
                      <VersionBadge
                        contentId={testRunAttempt?.inputAfterEachTestContentId || ''}
                        trunkContentId={testRunAttempt.outputAfterEachTestContent?.test?.trunkContentId || ''}
                        testId={testRunAttempt.outputAfterEachTestContent?.test?.id || ''}
                      />
                    </>
                  }
                  newStepsTitle={<Text className="font-medium">AI-modified Test</Text>}
                  gitBranch={testRun.testSuiteRun?.gitBranch ?? undefined}
                  testRunAttemptId={testRunAttempt?.id}
                />
              ),
            });
          }}
          className="mr-2"
        >
          AfterEach
        </Button>
      )}
    </>
  );
}
